import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { PhoneIncoming, SlidersHorizontal } from "@/lib/icons";
import { useState } from "react";
import { formatRelative, statusLabel, statusTone } from "@alm/domain";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { OffsetPaginationFooter } from "@/components/app/lists/offset-pagination-footer";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OptionSelect, SelectOption } from "@/components/ui/option-select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { useFacility } from "@/lib/facility/facility";
import { cn } from "@/lib/utils";
import { orpc } from "@/lib/orpc";
import { optionalDay, optionalInt, optionalOneOf, KEEP_PLACE } from "@/lib/search";
import { useCan } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import { CallDetailDialog } from "./-components/call-detail-dialog";
import { formatDuration } from "./-components/call-format";
import { LinkCallDialog } from "./-components/link-call-dialog";

type CallSearch = {
  matched?: "matched" | "unmatched";
  from?: string;
  to?: string;
  page?: number;
};

export const Route = createFileRoute("/_authenticated/_crm/call-logs/")({
  validateSearch: (search: Record<string, unknown>): CallSearch => ({
    matched: optionalOneOf(["matched", "unmatched"] as const, search.matched),
    from: optionalDay(search.from),
    to: optionalDay(search.to),
    page: optionalInt(search.page, 2, 100000),
  }),
  beforeLoad: ({ context }) => guardRoute(context.me, "call-logs"),
  component: CallLogs,
});

const LIMIT = 25;

interface Linkable {
  id: string;
  residentId: string | null;
  leadId: string | null;
}

function CallLogs() {
  const { facilityId } = useFacility();
  const canLink = useCan("crm:manage");
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = (next: Partial<CallSearch>) =>
    void navigate({
      search: (prev) => ({ ...prev, page: undefined, ...next }),
      ...KEEP_PLACE,
    });
  const matched: "all" | "matched" | "unmatched" = params.matched ?? "all";
  const setMatched = (next: "all" | "matched" | "unmatched") =>
    patch({ matched: next === "all" ? undefined : next });
  const from = params.from ?? "";
  const to = params.to ?? "";
  const setFrom = (next: string) => patch({ from: next || undefined });
  const setTo = (next: string) => patch({ to: next || undefined });
  const page = params.page ?? 1;
  const setPage = (next: number) =>
    void navigate({
      search: (prev) => ({ ...prev, page: next > 1 ? next : undefined }),
      ...KEEP_PLACE,
    });
  const [openCallId, setOpenCallId] = useState<string | null>(null);
  const [linking, setLinking] = useState<Linkable | null>(null);

  const calls = useQuery(
    orpc.crm.callLogs.list.queryOptions({
      input: {
        facilityId,
        ...(matched === "all" ? {} : { matched: matched === "matched" }),
        ...(from ? { from: new Date(`${from}T00:00:00`) } : {}),
        ...(to ? { to: new Date(`${to}T23:59:59`) } : {}),
        limit: LIMIT,
        offset: (page - 1) * LIMIT,
      },
    }),
  );
  const logs = calls.data?.rows ?? [];
  const total = calls.data?.total ?? 0;
  const start = (page - 1) * LIMIT;
  const activeCount = (from ? 1 : 0) + (to ? 1 : 0) + (matched === "all" ? 0 : 1);
  const reset = () => {
    void navigate({ search: {}, ...KEEP_PLACE });
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Call logs"
        count={calls.isPending || calls.isError ? undefined : total}
        meta="Inbound calls. Each one opens a triage task."
      />

      {/* Filters that can only return nothing wait until there is a call to filter. */}
      <div
        className={cn(
          "flex flex-wrap items-center gap-2",
          !calls.isPending && total === 0 && activeCount === 0 && "hidden",
        )}
      >
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline">
              <SlidersHorizontal className="size-4" />
              Filters
              {activeCount > 0 ? <Badge variant="secondary">{activeCount}</Badge> : null}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-72 space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="calls-from" className="text-xs">
                From
              </Label>
              <Input
                id="calls-from"
                type="date"
                value={from}
                onChange={(event) => {
                  setFrom(event.target.value);
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="calls-to" className="text-xs">
                To
              </Label>
              <Input
                id="calls-to"
                type="date"
                value={to}
                onChange={(event) => {
                  setTo(event.target.value);
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="calls-matched" className="text-xs">
                Caller match
              </Label>
              <OptionSelect
                id="calls-matched"
                className="w-full"
                value={matched}
                onChange={(event) => {
                  setMatched(event.target.value as typeof matched);
                }}
              >
                <SelectOption value="all">All calls</SelectOption>
                <SelectOption value="matched">Matched</SelectOption>
                <SelectOption value="unmatched">Unmatched</SelectOption>
              </OptionSelect>
            </div>
          </PopoverContent>
        </Popover>
        {activeCount > 0 ? (
          <Button variant="ghost" onClick={reset}>
            Clear filters
          </Button>
        ) : null}
      </div>

      {calls.isPending ? (
        <div className="flex flex-col gap-3">
          {[0, 1, 2, 3].map((index) => (
            <Skeleton key={`row-${index}`} className="h-16 w-full" />
          ))}
        </div>
      ) : calls.isError ? (
        <Card className="py-0">
          <ReadFailed
            className="p-4"
            message="Couldn't load call logs. None are shown because none were read."
            error={calls.error}
            retrying={calls.isFetching}
            onRetry={() => void calls.refetch()}
          />
        </Card>
      ) : logs.length === 0 ? (
        <Card className="py-0">
          <EmptyState
            title={activeCount > 0 ? "No calls match these filters" : "No calls yet"}
            description={
              activeCount > 0
                ? "Widen the date range or clear a filter to see more."
                : "Calls appear here automatically once a call integration in Settings answers one."
            }
            action={
              activeCount > 0 ? (
                <Button variant="outline" onClick={reset}>
                  Clear filters
                </Button>
              ) : undefined
            }
          />
        </Card>
      ) : (
        <>
          <Card className="gap-0 overflow-hidden py-0">
            <div>
              {logs.map((log) => {
                const unmatched = !(log.residentId || log.leadId);
                return (
                  // oxlint-disable-next-line jsx-a11y/control-has-associated-label -- named by the caller text inside
                  <button
                    key={log.id}
                    type="button"
                    onClick={() => setOpenCallId(log.id)}
                    className="hover:bg-accent focus-ring flex w-full flex-wrap items-start justify-between gap-3 border-b px-4.5 py-3 text-left last:border-b-0"
                  >
                    <span className="flex min-w-0 items-start gap-3">
                      <span className="bg-muted text-muted-foreground mt-0.5 inline-flex size-8 shrink-0 items-center justify-center rounded-md">
                        <PhoneIncoming className="size-4" />
                      </span>
                      <span className="min-w-0">
                        <span className="block font-medium">
                          {log.callerName || log.fromNumber || "Unknown caller"}
                        </span>
                        {log.fromNumber && log.callerName ? (
                          <span className="text-muted-foreground mt-0.5 block text-xs tabular-nums">
                            {log.fromNumber}
                          </span>
                        ) : null}
                        {log.summary ? (
                          <span className="text-muted-foreground mt-0.5 line-clamp-1 block text-sm">
                            {log.summary}
                          </span>
                        ) : null}
                      </span>
                    </span>
                    <span className="flex shrink-0 flex-col items-end gap-1.5">
                      <Badge variant={statusTone(log.status)}>{statusLabel(log.status)}</Badge>
                      <span className="text-muted-foreground flex items-center gap-3 text-xs tabular-nums">
                        {log.durationSeconds !== null ? (
                          <span>{formatDuration(log.durationSeconds)}</span>
                        ) : null}
                        {log.startedAt ? <span>{formatRelative(log.startedAt)}</span> : null}
                      </span>
                      <span className="flex flex-wrap justify-end gap-1">
                        {log.taskId ? <Badge variant="outline">Task</Badge> : null}
                        {log.residentId ? <Badge variant="outline">Resident</Badge> : null}
                        {log.leadId ? <Badge variant="outline">Lead</Badge> : null}
                        {unmatched ? <Badge variant="warn">Unmatched</Badge> : null}
                        {log.hasTranscript ? <Badge variant="outline">Transcript</Badge> : null}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
            {/* The pages turn inside the list's own card, as under every table. */}
            <OffsetPaginationFooter
              offset={start}
              pageSize={LIMIT}
              total={total}
              noun="calls"
              onOffsetChange={(offset) => setPage(offset / LIMIT + 1)}
              onPageSizeChange={() => undefined}
            />
          </Card>
        </>
      )}

      {openCallId ? (
        <CallDetailDialog
          callLogId={openCallId}
          canLink={canLink}
          onLink={(call) => {
            setOpenCallId(null);
            setLinking(call);
          }}
          onClose={() => setOpenCallId(null)}
        />
      ) : null}
      {linking ? (
        <LinkCallDialog
          callLogId={linking.id}
          leadId={linking.leadId}
          residentId={linking.residentId}
          onClose={() => setLinking(null)}
        />
      ) : null}
    </div>
  );
}
